// ── Marketplace ───────────────────────────────────────────────────────
// Loads from `strategy_listings WHERE status='live'`. Falls back to
// sample listings when no rows are visible (RLS-friendly).
// License button writes to `strategy_subscriptions`.

const MKT_FALLBACK = [
  { id:"m1", title:"Helios Alpha",       creator_short:"Marcus Chen",   rating:4.8, price_monthly:249, subscriber_count:124, category:"crypto",   tags:["BTC","mean-rev"],     blurb:"Mean-reversion tuned for high-volatility crypto majors. 4-hour bars.", perf_snapshot:{ d1:1.2,  d30:8.4,  y1:38.2, sharpe:2.1 } },
  { id:"m2", title:"Solstice Trend",     creator_short:"Aisha Patel",   rating:4.6, price_monthly:199, subscriber_count:89,  category:"equities", tags:["NASDAQ","daily"],     blurb:"Trend-following on top-25 NASDAQ names. Daily rebalance, low turnover.", perf_snapshot:{ d1:-0.4, d30:5.1,  y1:24.7, sharpe:1.6 } },
  { id:"m3", title:"Volatility Harvest", creator_short:"Jonas Krell",   rating:4.4, price_monthly:349, subscriber_count:61,  category:"options",  tags:["options","SPX"],      blurb:"Short-vol overlay using SPX weeklies. Hedged tail risk via VIX futures.", perf_snapshot:{ d1:0.8,  d30:-2.1, y1:18.9, sharpe:1.4 } },
  { id:"m4", title:"Momentum Cascade",   creator_short:"Yuki Tanaka",   rating:4.7, price_monthly:299, subscriber_count:203, category:"futures",  tags:["trend","ES"],         blurb:"Multi-timeframe momentum on ES futures. Intraday + swing layers.",         perf_snapshot:{ d1:2.1,  d30:9.8,  y1:41.3, sharpe:2.4 } },
  { id:"m5", title:"Pairs Alpha",        creator_short:"Sara Novak",    rating:4.5, price_monthly:199, subscriber_count:47,  category:"equities", tags:["pairs","neutral"],    blurb:"Statistical arbitrage on correlated equity pairs. Dollar-neutral.",        perf_snapshot:{ d1:0.3,  d30:3.2,  y1:16.4, sharpe:1.8 } },
  { id:"m6", title:"DeFi Cycle",         creator_short:"Alex Rivera",   rating:4.3, price_monthly:249, subscriber_count:38,  category:"crypto",   tags:["DeFi","on-chain"],    blurb:"On-chain signal driven strategy cycling DeFi blue-chips.",                  perf_snapshot:{ d1:-1.2, d30:12.3, y1:52.1, sharpe:1.9 } },
];

const MKT_CATS = ["crypto","equities","futures","options"];

const catColor = (c) => ({ crypto:"#11ff99", equities:"#60a5fa", futures:"#fbbf24", options:"#D946EF" })[c] || "#11ff99";

function MktCard({ s, onLicense, onView, licensing }) {
  const [hov, setHov] = React.useState(false);
  const perf = s.perf_snapshot || {};
  const d1  = perf.d1  ?? null;
  const d30 = perf.d30 ?? null;
  const y1  = perf.y1  ?? null;
  return (
    <div
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      className="card"
      style={{ border:`1px solid ${hov?"var(--border-strong)":"var(--border)"}`, display:"flex", flexDirection:"column", gap:14, transition:"all 0.2s", transform:hov?"translateY(-2px)":"none" }}>
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:8 }}>
        <div style={{ minWidth:0 }}>
          <div style={{ fontFamily:"var(--font-ui)", fontSize:14, fontWeight:600, color:"var(--ink)", letterSpacing:"-0.012em", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.title}</div>
          <div style={{ fontFamily:"var(--font-ui)", fontSize:12, color:"var(--muted)", marginTop:2 }}>{s.creator_short || "Creator"}</div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:4 }}>
          <Star size={11} color="var(--yellow)"/>
          <span style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--yellow)" }}>{(s.rating ?? 0).toFixed(1)}</span>
        </div>
      </div>
      <div style={{ display:"flex", gap:16 }}>
        {[{l:"1D",v:d1},{l:"30D",v:d30},{l:"1Y",v:y1}].map(p=>(
          <div key={p.l}>
            <div style={{ fontFamily:"var(--font-ui)", fontSize:10, color:"var(--subtle)" }}>{p.l}</div>
            <div style={{ fontFamily:"var(--font-mono)", fontSize:13, color: p.v == null ? "var(--muted)" : (p.v>0?"var(--green)":"var(--red)"), fontWeight:500 }}>
              {p.v == null ? "—" : ((p.v>0?"+":"") + p.v.toFixed(1) + "%")}
            </div>
          </div>
        ))}
        <div>
          <div style={{ fontFamily:"var(--font-ui)", fontSize:10, color:"var(--subtle)" }}>Subs</div>
          <div style={{ fontFamily:"var(--font-mono)", fontSize:13, color:"var(--body)" }}>{s.subscriber_count ?? 0}</div>
        </div>
      </div>
      <div style={{ borderRadius:6, overflow:"hidden", background:"var(--bg-deep)" }}>
        <Sparkline data={window.sparkFromSeed(s.id.charCodeAt(0)*7 + (s.id.charCodeAt(1)||0), 16)} color={catColor(s.category)} height={44}/>
      </div>
      <div style={{ fontFamily:"var(--font-ui)", fontSize:12, color:"var(--body)", lineHeight:1.55 }}>{s.blurb || s.description || "No description provided."}</div>
      <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
        {(s.tags || []).slice(0, 4).map(t=><span key={t} className="badge badge-muted" style={{ fontSize:10 }}>{t}</span>)}
        {s.category && <span className="badge" style={{ fontSize:10, background:`${catColor(s.category)}22`, color:catColor(s.category) }}>{s.category}</span>}
      </div>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", paddingTop:6, borderTop:"1px solid var(--border)" }}>
        <span style={{ fontFamily:"var(--font-mono)", fontSize:13, color:"var(--ink)", fontWeight:500 }}>${(s.price_monthly ?? 0).toFixed(0)}/mo</span>
        <div style={{ display:"flex", gap:6 }}>
          {s.strategy_id && <button className="btn btn-ghost btn-sm" onClick={()=>onView(s)}>View</button>}
          <button className="btn btn-primary btn-sm" disabled={licensing} onClick={()=>onLicense(s)}>
            {licensing ? <><RefreshCw size={10} style={{animation:"spin 1s linear infinite"}}/> Licensing…</> : "License"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MarketplacePage({ onToast }) {
  const [listings, setListings] = React.useState([]);
  const [source, setSource]     = React.useState("loading"); // loading|supabase|fallback
  const [licensingId, setLicensingId] = React.useState(null);
  const [confirmListing, setConfirmListing] = React.useState(null);

  // Filters
  const [query, setQuery]   = React.useState("");
  const [cats, setCats]     = React.useState([]);   // [] = all
  const [minRating, setMinRating] = React.useState(0);
  const [d30Min, setD30Min] = React.useState("");
  const [d30Max, setD30Max] = React.useState("");

  React.useEffect(() => { loadListings(); }, []);

  // Surface checkout return state once, then strip the query so refresh doesn't re-toast.
  React.useEffect(() => {
    const h = window.location.hash || "";
    const qIdx = h.indexOf("?");
    if (qIdx < 0) return;
    const qs = new URLSearchParams(h.slice(qIdx + 1));
    if (qs.get("subscribed")) {
      onToast?.("License active — subscription confirmed.");
      window.gqTrack?.('checkout_success', { listing_id: qs.get("subscribed") });
    } else if (qs.get("cancelled")) {
      onToast?.("Checkout cancelled.");
    } else {
      return;
    }
    history.replaceState(null, "", h.slice(0, qIdx));
  }, []);

  async function loadListings() {
    try {
      const { data, error } = await window.sbClient
        .from("strategy_listings")
        .select("*")
        .eq("status", "live")
        .in("visibility", ["marketplace","public_share"])
        .order("created_at", { ascending: false });
      if (error) throw error;
      if (data && data.length > 0) {
        // Enrich each row with a short creator stub (profiles is RLS-locked)
        const enriched = data.map(r => ({
          ...r,
          creator_short: r.creator_id ? `Creator ${r.creator_id.slice(0,6)}` : "—",
        }));
        setListings(enriched);
        setSource("supabase");
        return;
      }
    } catch (e) { console.warn("[marketplace] load failed", e); }
    setListings(MKT_FALLBACK);
    setSource("fallback");
  }

  const onLicense = async (s) => {
    setLicensingId(s.id);
    try {
      if (source !== "supabase") {
        onToast?.(`"${s.title}" is sample data — publish a real strategy to test checkout.`);
        setLicensingId(null);
        return;
      }
      const { data: { session } } = await window.sbClient.auth.getSession();
      if (!session) throw new Error("Not signed in");
      window.gqTrack?.('license_strategy', { listing_id: s.id });

      const resp = await fetch('/api/stripe/checkout', {
        method: 'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ listing_id: s.id, return_path: '#marketplace' }),
      });
      const out = await resp.json().catch(() => ({}));
      if (!resp.ok || !out.url) throw new Error(out.error || `Checkout failed (${resp.status})`);

      onToast?.(`Redirecting to checkout…`);
      window.location.href = out.url;
    } catch (e) {
      onToast?.(`License failed: ${e.message}`);
      setLicensingId(null);
    }
  };
  const onView = (s) => {
    // Open the listing's source strategy detail page if we have it; otherwise toast.
    if (s.strategy_id) window.navigateTo("strategy-detail", { id: s.strategy_id });
    else onToast?.(`Preview for "${s.title}" coming in v1.1`);
  };

  // Apply filters
  const filtered = listings.filter(s => {
    if (query) {
      const q = query.toLowerCase();
      if (!s.title?.toLowerCase().includes(q) && !s.creator_short?.toLowerCase().includes(q) && !(s.tags||[]).some(t => t.toLowerCase().includes(q))) return false;
    }
    if (cats.length > 0 && !cats.includes(s.category)) return false;
    if ((s.rating ?? 0) < minRating) return false;
    const d30 = s.perf_snapshot?.d30 ?? null;
    if (d30Min !== "" && (d30 == null || d30 < +d30Min)) return false;
    if (d30Max !== "" && (d30 == null || d30 > +d30Max)) return false;
    return true;
  });

  const toggleCat = (c) => setCats(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  const clearAll  = () => { setQuery(""); setCats([]); setMinRating(0); setD30Min(""); setD30Max(""); };
  const hasActiveFilter = query || cats.length || minRating > 0 || d30Min !== "" || d30Max !== "";

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      <PageHdr icon={Store} iconColor="var(--green)" iconBg="var(--green-dim)"
        title="Marketplace"
        subtitle={source === "supabase"
          ? `${listings.length} live listings`
          : "Discover and license top-performing strategies"}
        action={<div style={{display:"flex",gap:8,alignItems:"center"}}>
          {source === "fallback" && <span className="badge badge-muted" style={{fontSize:10}}>Sample listings</span>}
          <button className="btn btn-ghost btn-sm" onClick={()=>window.navigateTo?.('vault')}><Plus size={11}/> List a strategy</button>
        </div>}
      />

      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10 }}>
        <StatCard title="Strategies listed" value={listings.length.toLocaleString()}/>
        <StatCard title="Avg 30D return"     value={(() => {
          const r = listings.map(l => l.perf_snapshot?.d30).filter(v => v != null);
          if (!r.length) return "—";
          const m = r.reduce((a,b)=>a+b,0)/r.length;
          return (m>=0?"+":"") + m.toFixed(1) + "%";
        })()} accent="green"/>
        <StatCard title="Avg price" value={(() => {
          const p = listings.map(l => l.price_monthly).filter(v => v != null);
          if (!p.length) return "—";
          return "$" + Math.round(p.reduce((a,b)=>a+b,0)/p.length) + "/mo";
        })()} accent="amber"/>
      </div>

      {/* Sidebar + grid */}
      <div style={{ display:"grid", gridTemplateColumns:"220px 1fr", gap:16 }}>
        {/* Filter sidebar */}
        <div className="card" style={{ alignSelf:"flex-start", position:"sticky", top:12 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
            <span style={{ fontFamily:"var(--font-ui)", fontSize:13, fontWeight:600, color:"var(--ink)" }}>Filters</span>
            {hasActiveFilter && (
              <button className="btn btn-ghost btn-sm" style={{fontSize:10,padding:"2px 8px"}} onClick={clearAll}>Clear</button>
            )}
          </div>

          {/* Search */}
          <div style={{ position:"relative", marginBottom:14 }}>
            <Search size={12} color="var(--muted)" style={{ position:"absolute", left:9, top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }}/>
            <input className="input" value={query} onChange={e=>setQuery(e.target.value)} placeholder="Search…" style={{ paddingLeft:28, fontSize:11 }}/>
          </div>

          {/* Category checkboxes */}
          <div style={{ fontFamily:"var(--font-ui)", fontSize:10, color:"var(--muted)", marginBottom:6, letterSpacing:".05em" }}>CATEGORY</div>
          <div style={{ display:"flex", flexDirection:"column", gap:5, marginBottom:14 }}>
            {MKT_CATS.map(c => (
              <label key={c} style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer", fontFamily:"var(--font-ui)", fontSize:12, color:"var(--body)" }}>
                <input type="checkbox" checked={cats.includes(c)} onChange={()=>toggleCat(c)}/>
                <span style={{ width:6, height:6, borderRadius:"50%", background:catColor(c) }}/>
                {c}
              </label>
            ))}
          </div>

          {/* Rating slider */}
          <div style={{ fontFamily:"var(--font-ui)", fontSize:10, color:"var(--muted)", marginBottom:4, letterSpacing:".05em", display:"flex", justifyContent:"space-between" }}>
            <span>MIN RATING</span>
            <span style={{fontFamily:"var(--font-mono)", color:"var(--ink)"}}>{minRating.toFixed(1)}+</span>
          </div>
          <input type="range" min="0" max="5" step="0.1" value={minRating} onChange={e=>setMinRating(+e.target.value)} style={{ width:"100%", marginBottom:14 }}/>

          {/* 30D return range */}
          <div style={{ fontFamily:"var(--font-ui)", fontSize:10, color:"var(--muted)", marginBottom:6, letterSpacing:".05em" }}>30-DAY RETURN %</div>
          <div style={{ display:"flex", gap:6 }}>
            <input className="input" type="number" placeholder="Min" value={d30Min} onChange={e=>setD30Min(e.target.value)} style={{ padding:"4px 6px", fontSize:11 }}/>
            <input className="input" type="number" placeholder="Max" value={d30Max} onChange={e=>setD30Max(e.target.value)} style={{ padding:"4px 6px", fontSize:11 }}/>
          </div>
        </div>

        {/* Listing grid */}
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10, fontFamily:"var(--font-ui)", fontSize:11, color:"var(--muted)" }}>
            <span>{filtered.length} of {listings.length} listings</span>
            {source === "loading" && <span>Loading…</span>}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(290px,1fr))", gap:12 }}>
            {filtered.map(s=><MktCard key={s.id} s={s} onLicense={setConfirmListing} onView={onView} licensing={licensingId===s.id}/>)}
            {filtered.length===0 && (
              <div style={{ gridColumn:"1/-1" }}>
                <EmptyState
                  icon={Search}
                  title={hasActiveFilter ? "Nothing matches your filters." : "No strategies in the marketplace yet."}
                  body={hasActiveFilter ? "Try widening the rating threshold or clearing the 30-day return range." : "Be the first to publish — list one of your trading strategies and earn subscription revenue."}
                  primary={hasActiveFilter ? { label:"Clear filters", icon:Filter, onClick:clearAll } : { label:"List a strategy", icon:Plus, onClick:()=>window.navigateTo?.('vault') }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {confirmListing && (
        <div style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', backdropFilter:'blur(8px)',
          zIndex:7000, display:'flex', alignItems:'center', justifyContent:'center', padding:24
        }} onClick={e=>{ if(e.target===e.currentTarget) setConfirmListing(null); }}>
          <div style={{
            maxWidth:420, width:'100%', background:'var(--bg-card)',
            border:'1px solid var(--border-strong)', borderRadius:'var(--r-lg)', padding:28,
          }}>
            <h3 style={{fontFamily:"'Inter Tight','Inter',sans-serif", fontSize:18, fontWeight:700,
              color:'var(--ink)', marginBottom:8, letterSpacing:'-0.02em'}}>License this strategy?</h3>
            <p style={{fontFamily:'var(--font-ui)', fontSize:13, color:'var(--body)', lineHeight:1.65, marginBottom:6}}>
              <strong>{confirmListing.title}</strong> — ${confirmListing.price_monthly}/mo
            </p>
            <p style={{fontFamily:'var(--font-ui)', fontSize:12, color:'var(--muted)', marginBottom:20, lineHeight:1.65}}>
              You'll be redirected to Stripe to complete checkout. Cancel anytime from your vault. Recurring monthly — no contract.
            </p>
            <div style={{display:'flex', gap:8, justifyContent:'flex-end'}}>
              <button onClick={()=>setConfirmListing(null)} style={{
                background:'none', border:'1px solid var(--border-strong)', borderRadius:'var(--r-md)',
                padding:'7px 14px', cursor:'pointer', fontFamily:'var(--font-ui)', fontSize:13, color:'var(--muted)',
              }}>Cancel</button>
              <button onClick={async ()=>{
                const l = confirmListing;
                setConfirmListing(null);
                await onLicense(l);
              }} style={{
                background:'var(--primary)', border:'none', borderRadius:'var(--r-md)',
                padding:'7px 16px', cursor:'pointer', fontFamily:'var(--font-ui)', fontSize:13,
                color:'#fff', fontWeight:600,
              }}>Confirm license</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
window.MarketplacePage = MarketplacePage;
